class KitBuilder extends HTMLElement {
  constructor() {
    super();

    this.componentCount = parseInt(this.dataset.componentCount, 10);
    this.kitName = this.dataset.kitName;
    this.kitProductId = this.dataset.kitProductId;
    this.kitVariantId = this.dataset.kitVariantId;
    this.discountType = this.dataset.discountType || '';
    this.discountValue = parseFloat(this.dataset.discountValue) || 0;

    this.componentVariants = [];
    this.selectedVariants = [];
    this.selectedOptions = [];

    this.totalPriceEl = this.querySelector('[data-kit-total-price]');
    this.originalPriceEl = this.querySelector('[data-kit-original-price]');
    this.savingsBadgeEl = this.querySelector('[data-kit-savings-badge]');
    this.footerPriceEl = this.querySelector('[data-kit-footer-price]');
    this.footerOriginalEl = this.querySelector('[data-kit-footer-original]');
    this.atcButton = this.querySelector('[data-kit-atc]');
    this.atcButtonText = this.atcButton ? this.atcButton.querySelector('.add-text') : null;
    this.componentEls = this.querySelectorAll('[data-component-index]');
  }

  connectedCallback() {
    this._initComponents();
    this._bindEvents();
    this._updateTotal();
    this._updateButtonState();
    this._initStickyFooter();
  }

  _initComponents() {
    for (let i = 0; i < this.componentCount; i++) {
      const jsonEl = this.querySelector(`[data-component-variants="${i}"]`);
      const variants = jsonEl ? JSON.parse(jsonEl.textContent) : [];
      this.componentVariants[i] = variants;

      const compEl = this.querySelector(`[data-component-index="${i}"][data-component-simple]`);
      const isSimple = compEl && compEl.dataset.componentSimple === 'true';

      if (variants.length > 0) {
        const firstVariant = variants[0];
        this.selectedVariants[i] = firstVariant;
        this.selectedOptions[i] = firstVariant.options.slice();
      } else {
        this.selectedVariants[i] = null;
        this.selectedOptions[i] = [];
      }
    }
  }

  _bindEvents() {
    this.querySelectorAll('.kit-builder__pill-input').forEach((input) => {
      input.addEventListener('change', (e) => this._onOptionChange(e));
    });

    this.querySelectorAll('.kit-builder__select').forEach((select) => {
      select.addEventListener('change', (e) => this._onOptionChange(e));
    });

    if (this.atcButton) {
      this.atcButton.addEventListener('click', () => this._onAddToCart());
    } else {
      console.error('kit-builder: [data-kit-atc] button not found');
    }

    this.querySelectorAll('.kit-builder__tooltip-trigger').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const targetId = btn.dataset.tooltipTarget;
        const tooltip = this.querySelector(`#${targetId}`);
        if (!tooltip) return;
        const isVisible = tooltip.getAttribute('aria-hidden') === 'false';
        this.querySelectorAll('.kit-builder__tooltip-content').forEach((t) => {
          t.setAttribute('aria-hidden', 'true');
        });
        if (!isVisible) {
          tooltip.setAttribute('aria-hidden', 'false');
        }
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.kit-builder__tooltip-trigger')) {
        this.querySelectorAll('.kit-builder__tooltip-content').forEach((t) => {
          t.setAttribute('aria-hidden', 'true');
        });
      }
    });
  }

  _onOptionChange(evt) {
    const el = evt.currentTarget;
    const compIndex = parseInt(el.dataset.componentIndex, 10);
    const optionPosition = parseInt(el.dataset.optionPosition, 10);
    const value = el.value;

    if (el.type === 'radio') {
      const fieldset = el.closest('fieldset');
      if (fieldset) {
        const ci = parseInt(fieldset.dataset.componentIndex, 10);
        const op = parseInt(fieldset.dataset.optionPosition, 10);
        this.selectedOptions[ci][op - 1] = value;
        this._findMatchingVariant(ci);

        const labelEl = this.querySelector(`[data-selected-label="${ci}-${op}"]`);
        if (labelEl) labelEl.textContent = value;
        return;
      }
    }

    this.selectedOptions[compIndex][optionPosition - 1] = value || null;
    this._findMatchingVariant(compIndex);
  }

  _findMatchingVariant(compIndex) {
    const selected = this.selectedOptions[compIndex];
    const variants = this.componentVariants[compIndex];

    if (selected.some((opt) => opt === null || opt === '')) {
      this.selectedVariants[compIndex] = null;
      this._updateComponentPrice(compIndex);
      this._updateTotal();
      this._updateButtonState();
      return;
    }

    const match = variants.find((v) => v.options.every((opt, i) => selected[i] === opt));

    this.selectedVariants[compIndex] = match || null;
    this._updateComponentPrice(compIndex);
    this._updateTotal();
    this._updateButtonState();
  }

  _updateComponentPrice(compIndex) {
    const priceEl = this.querySelector(`[data-component-price="${compIndex}"]`);
    if (!priceEl) return;

    const variant = this.selectedVariants[compIndex];
    if (variant) {
      priceEl.textContent = Utility.formatMoney(variant.price);
    }
  }

  _applyDiscount(subtotal) {
    if (this.discountType === 'percentage' && this.discountValue > 0) {
      return Math.round(subtotal * (1 - this.discountValue / 100));
    } else if (this.discountType === 'fixed' && this.discountValue > 0) {
      return Math.max(0, subtotal - Math.round(this.discountValue * 100));
    }
    return subtotal;
  }

  _hasDiscount() {
    return (this.discountType === 'percentage' || this.discountType === 'fixed') && this.discountValue > 0;
  }

  _updateTotal() {
    let subtotal = 0;
    let allSelected = true;

    for (let i = 0; i < this.componentCount; i++) {
      const variant = this.selectedVariants[i];
      if (variant) {
        subtotal += variant.price;
      } else {
        allSelected = false;
      }
    }

    if (!allSelected) {
      if (this.totalPriceEl) this.totalPriceEl.textContent = '—';
      if (this.footerPriceEl) this.footerPriceEl.textContent = '—';
      if (this.originalPriceEl) this.originalPriceEl.textContent = '';
      if (this.footerOriginalEl) this.footerOriginalEl.textContent = '';
      return;
    }

    if (this._hasDiscount()) {
      const discounted = this._applyDiscount(subtotal);
      if (this.totalPriceEl) this.totalPriceEl.textContent = Utility.formatMoney(discounted);
      if (this.footerPriceEl) this.footerPriceEl.textContent = Utility.formatMoney(discounted);
      if (this.originalPriceEl) this.originalPriceEl.textContent = Utility.formatMoney(subtotal);
      if (this.footerOriginalEl) this.footerOriginalEl.textContent = Utility.formatMoney(subtotal);
    } else {
      if (this.totalPriceEl) this.totalPriceEl.textContent = Utility.formatMoney(subtotal);
      if (this.footerPriceEl) this.footerPriceEl.textContent = Utility.formatMoney(subtotal);
    }
  }

  _updateButtonState() {
    let allSelected = true;
    let allAvailable = true;

    for (let i = 0; i < this.componentCount; i++) {
      const variant = this.selectedVariants[i];
      if (!variant) {
        allSelected = false;
      } else if (!variant.available) {
        allAvailable = false;
      }
    }

    const textEl = this.atcButtonText;

    if (!allSelected) {
      this.atcButton.disabled = true;
      if (textEl) textEl.textContent = 'Select all options';
    } else if (!allAvailable) {
      this.atcButton.disabled = true;
      if (textEl) textEl.textContent = 'Sold out';
    } else {
      this.atcButton.disabled = false;
      if (textEl) textEl.textContent = 'Add to cart';
    }
  }

  _initStickyFooter() {
    const footer = this.querySelector('.kit-builder__footer');
    if (!footer || !this.atcButton) return;

    const mobileQuery = window.matchMedia('(max-width: 991.98px)');
    if (!mobileQuery.matches) return;

    const sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    sentinel.style.pointerEvents = 'none';
    footer.parentNode.insertBefore(sentinel, footer);

    const applyFixed = (fixed) => {
      if (fixed) {
        if (!footer.classList.contains('kit-builder__footer--fixed')) {
          footer.classList.add('kit-builder__footer--fixed');
          document.body.style.paddingBottom = `${footer.offsetHeight}px`;
        }
      } else {
        footer.classList.remove('kit-builder__footer--fixed');
        document.body.style.paddingBottom = '0px';
      }
    };

    const observer = new IntersectionObserver(([entry]) => applyFixed(!entry.isIntersecting), { threshold: 0 });
    observer.observe(sentinel);

    mobileQuery.addEventListener('change', (e) => {
      if (!e.matches) {
        applyFixed(false);
        observer.disconnect();
      } else {
        observer.observe(sentinel);
      }
    });
  }

  _onAddToCart() {
    if (this.atcButton.disabled) return;

    const kitId = `kit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const items = [];
    for (let i = 0; i < this.componentCount; i++) {
      const variant = this.selectedVariants[i];
      if (!variant) return;

      const compEl = this.querySelector(`[data-component-index="${i}"][data-component-simple]`);
      const labelEl = compEl ? compEl.querySelector('.kit-builder__card-name') : null;
      const componentLabel = labelEl ? labelEl.textContent.trim() : variant.title;

      const variantDetail = variant.title !== 'Default Title' ? variant.title : '';

      items.push({
        id: variant.id,
        quantity: 1,
        properties: {
          _kit_id: kitId,
          _kit_name: this.kitName,
          _component: componentLabel,
          _component_variant: variantDetail,
          _kit_product_id: this.kitProductId,
          _kit_variant_id: this.kitVariantId,
        },
      });
    }

    Utility.setLoadingState(true, this.atcButton);

    const body = JSON.stringify({ items: items });

    fetch(routes.cart_add_url + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: body,
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw text ? JSON.parse(text) : { message: `Error ${response.status}` };
          });
        }
        return response.json();
      })
      .then((data) => {
        if (data.errors) {
          const msg = data.description || data.errors || 'Could not add to cart';
          Utility.handleError(msg, 'Add to Cart');
          return;
        }

        const cartElement = document.querySelector('cart-form');
        if (cartElement) {
          const sectionId = cartElement.dataset.sectionId;
          fetch(`${routes.cart_fetch_url}?sections=${sectionId}`)
            .then((r) => r.json())
            .then((sections) => {
              eventBus.publish(PUB_SUB_EVENTS.cartUpdate, {
                data: { action: 'add-item', item: data, sections: sections },
              });
              cartElement._updateCart(sections, this.atcButton);
            });
        } else {
          updateCartCountInHeader();
          Utility.handleSuccess(this.kitName, 'Added to Cart');
        }
      })
      .catch((e) => {
        const msg = typeof e === 'object' ? e.description || e.message || 'Could not add to cart' : e;
        Utility.handleError(msg, 'Add to Cart');
      })
      .finally(() => {
        Utility.setLoadingState(false, this.atcButton);
      });
  }
}

customElements.define('kit-builder', KitBuilder);
